import * as React from 'react';
import { Button } from '@material-ui/core';
import { httpClient } from '../utils/dataProvider';
import {
  Empty, PageHeader, Row, RowMeta, RowText, RowTitle, Search,
  StatusPill, Surface, Toolbar, MetricCard, Select, ActionButton
} from '../components/ui';

const REVIEW_STATUSES = ['pending', 'approved', 'rejected'];

const CREDENTIAL_TYPE_LABELS = {
  syndicate_card: 'Syndicate Card',
  university_degree: 'University Degree',
  professional_license: 'Professional License',
  company_registration: 'Company Registration',
  insurance: 'Insurance',
  other: 'Other',
};

const REVIEW_STATUS_TONES = {
  pending: 'pending',
  approved: 'active',
  rejected: 'banned',
};

const linkStyle = { color: '#7c3aed', textDecoration: 'none', cursor: 'pointer', fontWeight: 600 };

export default function CredentialsReview() {
  const [credentials, setCredentials] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('pending');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [reviewLoading, setReviewLoading] = React.useState(null);

  const loadCredentials = React.useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.append('status', statusFilter);
    httpClient(`https://skilzapp.net/api/userservices/credentials/pending?${params}`)
      .then(({ json }) => {
        // Flatten: API returns UserService docs; expand credentials array
        const flat = [];
        (json.docs || []).forEach(us => {
          const provider = us.createdBy || {};
          const svc = us.defaultService || {};
          (us.credentials || []).forEach(cred => {
            flat.push({
              ...cred,
              userServiceId: us._id,
              userId: provider._id,
              providerName: provider.name,
              providerPhone: provider.phone,
              serviceName: svc.name,
              credentialId: cred._id,
            });
          });
        });
        setCredentials(flat);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter]);

  React.useEffect(() => { loadCredentials(); }, [loadCredentials]);

  const filtered = React.useMemo(() => {
    let result = credentials;
    if (typeFilter !== 'all') {
      result = result.filter(c => c.type === typeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.providerName || '').toLowerCase().includes(q) ||
        (c.serviceName || '').toLowerCase().includes(q) ||
        (c.label || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [credentials, typeFilter, search]);

  const counts = React.useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    credentials.forEach(cred => {
      const s = cred.reviewStatus || 'pending';
      if (c[s] !== undefined) c[s]++;
    });
    return c;
  }, [credentials]);

  const handleReview = async (userServiceId, credentialId, action, rejectionReason) => {
    if (action === 'rejected' && !rejectionReason) {
      rejectionReason = window.prompt('Rejection reason:');
      if (!rejectionReason) return;
    }
    setReviewLoading(credentialId);
    try {
      await httpClient(`https://skilzapp.net/api/userservices/${userServiceId}/credentials/${credentialId}/review`, {
        method: 'PUT',
        body: JSON.stringify({ status: action, rejectionReason }),
      });
      loadCredentials();
    } catch (err) {
      console.error('Review failed:', err);
      alert('Review action failed. Check console for details.');
    } finally {
      setReviewLoading(null);
    }
  };

  return (
    <main className="sk-page">
      <PageHeader
        eyebrow="Verification"
        title="Credentials Review"
        description="Review and approve provider credentials and professional documents."
      />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <MetricCard label="Pending" value={counts.pending} detail="Awaiting review" tone="orange" />
        <MetricCard label="Approved" value={counts.approved} detail="Verified credentials" />
        <MetricCard label="Rejected" value={counts.rejected} detail="Rejected credentials" tone="purple" />
      </div>

      <Surface
        title="Credential Queue"
        description={`${filtered.length} credential${filtered.length !== 1 ? 's' : ''}`}
      >
        <Toolbar
          right={<Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search provider, service…" />}
        >
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Select
              label="Status"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              options={REVIEW_STATUSES}
            />
            <Select
              label="Type"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              options={Object.keys(CREDENTIAL_TYPE_LABELS)}
            />
          </div>
        </Toolbar>

        {loading && <div className="sk-state is-loading" role="status">Loading…</div>}
        {!loading && !filtered.length && <Empty>No credentials found matching your filters.</Empty>}

        {filtered.map(cred => (
          <Row
            key={cred._id || cred.credentialId}
            right={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                <StatusPill status={REVIEW_STATUS_TONES[cred.reviewStatus] || 'pending'}>
                  {cred.reviewStatus || 'pending'}
                </StatusPill>
                {cred.reviewStatus === 'pending' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <ActionButton
                      variant="primary"
                      disabled={reviewLoading === cred.credentialId}
                      onClick={() => handleReview(cred.userServiceId, cred.credentialId, 'approved')}
                      style={{ fontSize: 11, padding: '4px 12px', minHeight: 32 }}
                    >
                      {reviewLoading === cred.credentialId ? '…' : '✓ Approve'}
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      disabled={reviewLoading === cred.credentialId}
                      onClick={() => handleReview(cred.userServiceId, cred.credentialId, 'rejected')}
                      style={{ fontSize: 11, padding: '4px 12px', minHeight: 32 }}
                    >
                      {reviewLoading === cred.credentialId ? '…' : '✕ Reject'}
                    </ActionButton>
                  </div>
                )}
              </div>
            }
          >
            <RowText>
              <RowTitle>
                <a href={`#/users/${cred.userId}/show`} style={linkStyle}>
                  {cred.providerName || 'Unknown Provider'}
                </a>
              </RowTitle>
              <RowMeta>
                Service: <a href={`#/userservices/${cred.userServiceId}/show`} style={linkStyle}>
                  {cred.serviceName || '—'}
                </a>
                {' · '}Type: {CREDENTIAL_TYPE_LABELS[cred.type] || cred.type || '—'}
                {cred.label && ` · ${cred.label}`}
              </RowMeta>
              {cred.fileURL && (
                <RowMeta>
                  <a href={`https://skilzcdn.nyc3.cdn.digitaloceanspaces.com/${cred.fileURL}`} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                    View Document ↗
                  </a>
                </RowMeta>
              )}
              <RowMeta dim>
                Uploaded: {cred.uploadedAt ? new Date(cred.uploadedAt).toLocaleString() : '—'}
                {cred.reviewedAt && ` · Reviewed: ${new Date(cred.reviewedAt).toLocaleString()}`}
                {cred.rejectionReason && ` · Reason: ${cred.rejectionReason}`}
              </RowMeta>
            </RowText>
          </Row>
        ))}
      </Surface>
    </main>
  );
}
